<template>
  <div data-private class="">

    <!-- Loading State -->
    <div v-if="loading" class="items-center justify-center flex" >
      <div class="w-40 h-40">
      <Spinning_Loader />
      </div>
    </div>

    <!-- No Data State -->
    <div v-else-if="!show_data" class="text-gray-500 py-2 text-center">
      No medication adherence data available
    </div>

    <!-- Data Display -->
    <div v-else class=" rounded ">
      <div class="grid grid-cols-10 p-2 text-sm">
        <!-- Patient -->
        <div class="col-span-3 font-semibold">Patient Identifier: </div>
        <div class="sensitive col-span-7">{{taskMetadata.patient_identifier}}</div>
        <div class="col-span-3 font-semibold">Patient Name: </div>
        <div class="sensitive col-span-7">{{taskMetadata.bene_last_name}}, {{taskMetadata.bene_first_name}}</div>
        <div class="col-span-3 font-semibold">Patient DOB: </div>
        <div class="sensitive col-span-7">{{taskMetadata.bene_dob}}</div>
        <div class="col-span-3 font-semibold">Patient Phone: </div>
        <div class="sensitive col-span-7">{{ formatPhoneNumber(condition_data[0].patient_phone_number) }}</div>

        <!-- Provider / Prescribing / Pharmacy -->
        <div class="col-span-3 font-semibold">PCP NPI: </div>
        <div class="sensitive col-span-7">{{taskMetadata.bene_pcp_npi || 'N/A' }}</div>
        <div class="col-span-3 font-semibold">Prescriber Name: </div>
        <div class="sensitive col-span-7">{{ prescriberName }}</div>
        <div class="col-span-3 font-semibold">Prescriber NPI: </div>
        <div class="sensitive col-span-7">{{ prescriberNpi }}</div>
        <div class="col-span-3 font-semibold">Prescriber Phone: </div>
        <div class="sensitive col-span-7">{{ formatPhoneNumber(condition_data[0].provider_phone_number) }}</div>
        <div class="col-span-3 font-semibold">Pharmacy Name: </div>
        <div class="sensitive col-span-7">{{ condition_data[0].pharmacy_name || 'N/A' }}</div>
        <div class="col-span-3 font-semibold">Pharmacy Phone: </div>
        <div class="sensitive col-span-7">{{ formatPhoneNumber(condition_data[0].pharmacy_phone_number) }}</div>
      </div>
      <div v-for="condition in condition_data" :key="condition.condition_type"
           class="w-full bg-gray-50 rounded-md p-4 shadow-md text-sm mt-4" >
        <div class="grid grid-cols-10 gap-y-1 items-center">
          <!-- Patient Info -->
          <div class="col-span-3 text-gray-600">Drug Type:</div>
          <div class="col-span-7 ">{{ condition.condition_type==='DBTS'? 'Diabetes' : condition.condition_type==='STATN' ? 'Cholesterol' :'Hypertension' }}</div>
          <div class="col-span-3 text-gray-600">Drug Name:</div>
          <div class="col-span-7 ">{{ condition.medication_name }}</div>
          <div class="col-span-3 text-gray-600 mt-">PDC Score:</div>
          <div class="col-span-7 flex items-center gap-2">
            <template v-if="condition.pdc_parsed != null && condition.pdc_parsed !== ''">
              <span class="font-semibold" :class="getPDCColorClass(condition.pdc_parsed)">
                {{ Math.round(condition.pdc_parsed * 100) }}%
              </span>
              <!-- If metric already failed -->
              <span
                v-if="condition.already_failed_metric"
                class="px-2 py-0.5 rounded text-xs font-medium bg-red-600 text-white"
              >
                FAILED
              </span>
              <!-- Normal pass/fail state -->
              <span
                v-else
                class="px-2 py-0.5 rounded text-xs font-medium"
                :class="condition.measure_passed ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'"
              >
                {{ condition.measure_passed ? 'PASSING' : 'FAILING' }}
              </span>
            </template>
            <template v-else>
              <span class="px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700">
                FIRST FILL
              </span>
            </template>
          </div>
          <div class="col-span-3 text-gray-600">Last Day to Fail:</div>
          <div
            class="col-span-7 font-medium"
            :class="condition.already_failed_metric ? 'text-red-600' : (condition.lastdaytofail ? getDaysToFailureColorClass(condition.lastdaytofail) : 'text-gray-400')"
          >
            {{ condition.already_failed_metric ? 'FAILED' : (condition.lastdaytofail ? condition.lastdaytofail : 'N/A — First Fill') }}
          </div>

          <div class="col-span-3 text-gray-600">YTD Missed:</div>
          <div class="col-span-7 font-medium">{{ condition.ytd_missed_days }} days</div>

          <!-- Prescription Details -->
          <div class="col-span-3 text-gray-600">First Filled:</div>
          <div class="col-span-7">{{ formatDate(condition.first_filled_dt) }}</div>

          <div class="col-span-3 text-gray-600">Last Filled:</div>
          <div class="col-span-7">{{ formatDate(condition.last_filled_dt) }}</div>

          <div class="col-span-3 text-gray-600">Next Fill:</div>
          <div class="col-span-7 flex items-center gap-2">
            <span>{{ formatDate(condition.next_filled_dt) }}</span>
            <span v-if="isOverdue(condition.next_filled_dt)" class="text-red-600 font-medium text-xs">
              {{ condition.next_filled_dt < condition.observation_date ? 'OVERDUE' : 'POTENTIALLY OVERDUE'  }}
            </span>
          </div>

          <div class="col-span-3 text-gray-600">Refills:</div>
          <div class="col-span-7 font-medium" :class="condition.refillsremaining === 0 ? 'text-red-600' : ''">
            {{ condition.refillsremaining }}
          </div>

          <!-- Status -->
          <div class="col-span-10 flex items-center gap-2 mt-2">
            <span v-if="condition.has_current_prescription" class="text-blue-600 font-medium text-xs">
              ACTIVE PRESCRIPTION
            </span>
          </div>

          <!-- Last Updated -->
          <div class="col-span-10 text-xs text-gray-500 pt-2 border-t border-gray-200">
            Last report update: {{ formatDate(condition.report_date) }}
          </div>
        </div>
      </div>

          <!-- Work Logger Integration -->
    </div>

  </div>
</template>

<script>
import axios from 'axios'
import Spinning_Loader from '@/components/widgetcomponents/Viz_Elements/Spinning_Loader.vue';
import Field_Errors from '@/components/widgetcomponents/Field_Errors.vue';
import WorkLogger from '@/components/Portal/Task_Tables/Work_Widgets/WorkLogger.vue';
import Work_Timeline from "@/components/Portal/Task_Tables/Work_Widgets/Work_Timeline.vue";

export default {
  components: {
    Work_Timeline,
    Field_Errors,
    Spinning_Loader,
    WorkLogger
  },
  props: {
    taskType: {
      type: Number,
      required: true
    },
    taskTypeName:{
      type: String,
      required: true
    },
    taskMetadata: {
      type: Object,
      required: true
    },
    opportunities: {
      type: Array,
      default: () => []
    }
  },
  data(){
    return {
      condition_data: [],
      show_data: false,
      loading: true,
    }
  },
  computed: {
    prescriberName() {
      if (!this.condition_data.length) return 'N/A';
      return this.condition_data[0].provider_name || this.condition_data[0].prescriber_name || 'N/A';
    },
    prescriberNpi() {
      if (!this.condition_data.length) return 'N/A';
      return this.condition_data[0].npi || this.condition_data[0].prescriber_npi || 'N/A';
    },
  },
  methods: {
    formatPhoneNumber(phoneNumber) {
      if (!phoneNumber) return 'N/A'

      // Convert to string and handle float formatting
      let phoneStr = phoneNumber.toString()

      // Remove trailing .0 or other decimal digits if present
      if (phoneStr.includes('.')) {
        phoneStr = phoneStr.split('.')[0]
      }

      // Remove all non-digit characters
      const cleaned = phoneStr.replace(/\D/g, '')

      // Check if we have a valid 10-digit number
      if (cleaned.length === 10) {
        return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`
      }

      // If not 10 digits, return as-is or handle differently
      return phoneNumber
    },
    async fetchRelevantClaims() {
      if (!this.taskMetadata.patient_identifier) {
        console.error('Error fetching encounter data: Missing patient identifier')
        this.show_data = false
        this.loading = false
        return
      }

      const request_data = {
        metric_id: 45,
        metric_insight_id: 1,
        insight_type: 'task_insight',
        sql_filters: {
          patient_identifier: this.taskMetadata['patient_identifier'],
        }
      }

      try {
        const response = await axios.post(
          'api/returnmetricinsight/',
          request_data,
        )

        if (response?.data?.length > 0) {
          this.condition_data = response.data
          this.show_data = true
        } else {
          this.show_data = false
        }
      } catch (error) {
        console.error('Error fetching encounter data:', error)
        this.show_data = false
      } finally {
        this.loading = false
      }
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A'
      const [datePart] = dateString.split('T');
      const [year, month, day] = datePart.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString('en-US', {
        month: 'numeric',
        day: 'numeric',
        year: '2-digit'
      })
    },
    getPDCColorClass(pdc) {
      if (pdc >= 0.8) return 'text-green-600'
      if (pdc >= 0.6) return 'text-yellow-600'
      return 'text-red-600'
    },
    getDaysToFailureColorClass(lastdaytofail) {
      if (!lastdaytofail || lastdaytofail === 'FAILED') return 'text-red-600'

      const failureDate = new Date(lastdaytofail)
      const today = new Date()
      const daysUntilFailure = Math.ceil((failureDate - today) / (1000 * 60 * 60 * 24))

      if (daysUntilFailure <= 7) return 'text-red-600'
      if (daysUntilFailure <= 30) return 'text-yellow-600'
      return 'text-green-600'
    },
    isOverdue(dateString) {
      if (!dateString) return false
      const dueDate = new Date(dateString)
      const today = new Date()
      return dueDate < today
    },

  },
  mounted() {
    this.$emit('can_submit',true,'')
    this.fetchRelevantClaims()
  }
}
</script>

<style scoped>
/* Add any component-specific styles here if needed */
</style>
